import React, { forwardRef, useState } from "react";
import { toJS } from "mobx";
import { useLocalStore, Observer, useObserver } from "mobx-react";
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  OpenInNew,
  SaveAlt,
  SettingsOutlined,
  PostAddOutlined,
  PlaylistAddCheck
} from "@material-ui/icons";
import { WithTooltip } from "@components";
import { SpinnerOverlay, Spinner } from "@common";
import { BookingFinanceSummary } from "./BookingFinanceSummary";
import locale from "@bonliva-crm/locale";
import { BookingInvoicingStore, payFormWeekDayOptions } from "./BookingInvoicingStore";
import { FormNote } from "./FormNote";
import { AdditionalPayoutInput } from "./AdditionalPayoutInput";
import { useBookingInvoiceDialog } from "../BookingInvoiceDialog/useBookingInvoiceDialog";
import { useBookingSubcontractorInvoiceDialog } from "../BookingSubcontractorInvoiceDialog/useBookingSubcontractorInvoiceDialog";
import Select from "@common/Select";
import { Form, ToggleButtonGroup } from "@components/formx";
import { InvoiceMonth, InvoiceSplitType } from "@bonliva-crm/shared";
// import { PublishSubcontractorFlowListItem } from "./subcontractor/publish-subcontractor-flow-list-item";

type Props = {
  bookingId: string;
  bookingDate: Date;
  costCenterId: string;
  consultantFirstName: string;
  consultantLastName: string;
  isOwnProject: boolean;
  isProjectManager: boolean;
  onFormClosed: () => void;
  missedFields: string[];
  canAddPayForm: boolean;
  isDirtyBookingFinanceForm: boolean;
  isGroupInvoicing: boolean;
  consultantType: "subcontractor" | "employee" | "";
  billingInstructions?: string;
};

export const BookingInvoicing = forwardRef<BookingInvoicingStore, Props>((props, ref) => {
  const styles = useStyles({});

  const store = useLocalStore(
    () =>
      new BookingInvoicingStore(
        props.bookingId,
        props.onFormClosed,
        props.bookingDate,
        props.consultantFirstName,
        props.consultantLastName,
        props.isGroupInvoicing
      )
  );

  const [showOptionsDialog, setOptionsDialog] = useState<string | null>(null);

  const invoiceDialog = useBookingInvoiceDialog({
    withCustomNumber: false,
    costCenterId: props.costCenterId,
    onSubmit: async ({ invoiceNumberShort, month, type }) => {
      store.appendInvoiceFormToFortnox(invoiceNumberShort, month, type);
    }
  });

  const subcontractorInvoiceDialog = useBookingSubcontractorInvoiceDialog({
    costCenterId: props.costCenterId,
    onSubmit: async ({ invoiceNumber }) => {
      store.appendSubcontractorInvoiceFormToFortnox(invoiceNumber);
    }
  });

  if (!store.allowed) {
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const formatTooltip = useObserver(() => {
    return (
      !props.canAddPayForm && (
        <div style={{ fontSize: "1rem" }}>
          <p>
            <strong>{locale.ConsultantMissingField}:</strong>
          </p>
          {toJS(props.missedFields)?.map((x, i) => (
            <p key={i}>{x}</p>
          ))}
          <p>{locale.PleaseFillBeforeContinue}</p>
        </div>
      )
    );
  });

  (ref as any).current = store;

  return (
    <Observer>
      {() => (
        <div style={{ position: "relative" }}>
          <SpinnerOverlay show={store.loading} />

          {store.error && <Typography color={"error"}>Cannot load data</Typography>}

          <List component={Card}>
            <ListSubheader>Faktura</ListSubheader>

            <WithTooltip
              title={
                props.isDirtyBookingFinanceForm
                  ? "Fakturaformuläret måste sparas innan du kan öppna"
                  : ""
              }
            >
              <ListItem
                button
                dense
                disabled={!store.canCreateInvoice || props.isDirtyBookingFinanceForm}
                onClick={() => {
                  if (!(!store.canCreateInvoice || props.isDirtyBookingFinanceForm)) {
                    store.goToInvoiceForm();
                  }
                }}
              >
                <ListItemText primary={locale.FillInvoiceForm} />
                <ListItemSecondaryAction>
                  <IconButton
                    disabled={!store.canDownloadInvoiceForm}
                    size={"small"}
                    onClick={(e) =>
                      store.openContractById(
                        e,
                        store.data?.invoiceContractId,
                        store.data?.invoiceContractName
                      )
                    }
                  >
                    <WithTooltip title={store.data?.invoiceContractName}>
                      <PlaylistAddCheck />
                    </WithTooltip>
                  </IconButton>
                  <IconButton
                    disabled={!store.canDownloadInvoiceForm}
                    size={"small"}
                    onClick={(e) => store.downloadCustomerInvoiceForm(e)}
                  >
                    <SaveAlt />
                  </IconButton>

                  <IconButton
                    disabled={!store.canDownloadInvoiceForm}
                    size={"small"}
                    onClick={(e) => store.previewCustomerInvoiceForm(e)}
                  >
                    <OpenInNew />
                  </IconButton>

                  <IconButton
                    disabled={!store.canDownloadInvoiceForm}
                    size={"small"}
                    onClick={() => setOptionsDialog("invoice")}
                  >
                    <SettingsOutlined />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </WithTooltip>

            <ListItem
              button
              dense
              disabled={!store.canCreateFortnoxInvoice}
              onClick={(e) => store.toggleFortnoxInvoiceDialog(e)}
              className={styles.listItemWithInput}
            >
              <ListItemText primary={locale.CreateInvoiceFormFortnox} />

              {store.isCreatingFortnoxInvoice && (
                <Spinner inline size={14} style={{ marginLeft: 8 }} />
              )}

              {props.isGroupInvoicing && (
                <Button
                  size="small"
                  disabled={!store.canCreateFortnoxInvoice}
                  onClick={(e) => {
                    invoiceDialog.open();
                    e.stopPropagation();
                  }}
                >
                  <PostAddOutlined fontSize="small" /> Samlingsfaktura
                </Button>
              )}
            </ListItem>

            {store.data?.fortnoxInvoiceIds?.map((x) => (
              <ListItem
                key={x}
                button
                dense
                disabled={!store.canPreviewFortnoxInvoice}
                onClick={() => store.previewFortnoxUrl(x)}
              >
                <ListItemText
                  primary={`${locale.PreviewInvoiceFormFortnox} (${
                    locale.InvoiceNumber
                  }: ${x ?? "n/a"})`}
                />
              </ListItem>
            ))}

            <ListItem>
              <FormNote
                id="invoice"
                label={locale.NotesForInvoiceForm}
                initialValue={store.data?.invoiceFormNote || ""}
                onChange={(v) => store.saveInvoiceFormNote(v)}
                isLoading={store.loading}
              />
            </ListItem>

            {!!props.billingInstructions && (
              <ListItem>
                <TextField
                  disabled
                  fullWidth
                  label={locale.billingInstructions}
                  value={props.billingInstructions}
                  InputProps={{
                    multiline: true
                  }}
                />
              </ListItem>
            )}
          </List>

          {props.consultantType === "employee" && (
            <List component={Card} style={{ marginTop: 16 }}>
              <ListSubheader>{locale.ConsultantPaymentForm}</ListSubheader>

              <WithTooltip
                title={
                  props.isDirtyBookingFinanceForm
                    ? "Fakturaformuläret måste sparas innan du kan öppna"
                    : ""
                }
              >
                <ListItem
                  button
                  dense
                  disabled={!store.canCreatePaymentForm}
                  onClick={() => {
                    if (store.canCreatePaymentForm) {
                      store.goToPaymentForm();
                    }
                  }}
                >
                  <WithTooltip
                    placement={"top-start"}
                    title={
                      props.missedFields
                        ? formatTooltip
                        : locale.FillConsultantPaymentForm
                    }
                    aria-label="add"
                  >
                    <ListItemText primary={locale.FillConsultantPaymentForm} />
                  </WithTooltip>
                  <ListItemSecondaryAction>
                    <IconButton
                      disabled={!store.canDownloadPaymentForm}
                      size={"small"}
                      onClick={(e) =>
                        store.openContractById(
                          e,
                          store.data?.paymentContractId,
                          store.data?.paymentContractName
                        )
                      }
                    >
                      <WithTooltip title={store.data?.paymentContractName}>
                        <PlaylistAddCheck />
                      </WithTooltip>
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadPaymentForm}
                      size={"small"}
                      onClick={(e) => store.downloadPaymentForm(e)}
                    >
                      <SaveAlt />
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadPaymentForm}
                      size={"small"}
                      onClick={(e) => store.previewPaymentForm(e)}
                    >
                      <OpenInNew />
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadPaymentForm}
                      size={"small"}
                      onClick={() => setOptionsDialog("payment")}
                    >
                      <SettingsOutlined />
                    </IconButton>{" "}
                  </ListItemSecondaryAction>
                </ListItem>
              </WithTooltip>

              <ListItem
                button
                dense
                disabled={!store.canSendPaymentToFortnox || store.isCheckingFortnox}
                onClick={(e) => store.handleSendPayFormClick(e)}
              >
                <ListItemText
                  primary={`${locale.SendConsultantPaymentToFortnox} (Datum: ${store.payFormPublishedToFortnox})`}
                />
                {(store.isSendingPaymentToFortnox || store.isCheckingFortnox) && (
                  <Spinner inline size={14} style={{ marginLeft: 8 }} />
                )}
              </ListItem>

              {store.showPayFormAdditionalPayoutInput && (
                <ListItem>
                  <AdditionalPayoutInput
                    id="payment"
                    initialAmount={store.bookingData?.additionalPayoutAmount ?? null}
                    initialDescription={
                      store.bookingData?.additionalPayoutDescription ?? ""
                    }
                    onSave={(input) => store.saveAdditionalPayout(input)}
                    isLoading={store.loading}
                  />
                </ListItem>
              )}

              <ListItem>
                <FormNote
                  id="payment"
                  label={locale.NotesForPayForm}
                  initialValue={store.data?.paymentFormNote || ""}
                  onChange={(v) => store.savePayFormNote(v)}
                  isLoading={store.loading}
                />
              </ListItem>
            </List>
          )}

          {props.consultantType === "employee" && store.sickTimeExist && (
            <List component={Card} style={{ marginTop: 16 }}>
              <ListSubheader>{locale.SickPaymentForm}</ListSubheader>

              <WithTooltip
                title={
                  props.isDirtyBookingFinanceForm
                    ? "Fakturaformuläret måste sparas innan du kan öppna"
                    : ""
                }
              >
                <ListItem
                  disabled={props.isDirtyBookingFinanceForm || !store.canCreateSickForm}
                  button
                  dense
                  onClick={(event) => {
                    if (!props.isDirtyBookingFinanceForm && store.canCreateSickForm) {
                      store.openSickForm(event);
                    }
                  }}
                >
                  <ListItemText primary={locale.ManageSickForm} />
                  <ListItemSecondaryAction>
                    <IconButton
                      disabled={!store.canDownloadSickForm}
                      size={"small"}
                      onClick={(e) =>
                        store.openContractById(
                          e,
                          store.data?.sickContractId,
                          store.data?.sickContractName
                        )
                      }
                    >
                      <WithTooltip title={store.data?.sickContractName}>
                        <PlaylistAddCheck />
                      </WithTooltip>
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadSickForm}
                      size={"small"}
                      onClick={(e) => store.downloadSickPaymentForm(e)}
                    >
                      <SaveAlt />
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadSickForm}
                      size={"small"}
                      onClick={(e) => store.previewSickPaymentForm(e)}
                    >
                      <OpenInNew />
                    </IconButton>
                    <IconButton
                      disabled={!store.canDownloadSickForm}
                      size={"small"}
                      onClick={() => setOptionsDialog("sick-payment")}
                    >
                      <SettingsOutlined />
                    </IconButton>{" "}
                  </ListItemSecondaryAction>
                </ListItem>
              </WithTooltip>
              <ListItem
                button
                dense
                disabled={!store.canSendSickFormToFortnox || store.isCheckingFortnox}
                onClick={(e) => store.handleSendSickFormClick(e)}
              >
                <ListItemText
                  primary={`${locale.SendConsultantSickFormToFortnox} (Datum: ${store.sickFormPublishedToFortnox})`}
                />
                {(store.isSendingSickFormToFortnox || store.isCheckingFortnox) && (
                  <Spinner inline size={14} style={{ marginLeft: 8 }} />
                )}
              </ListItem>

              <ListItem>
                <FormNote
                  id="sick-pay"
                  label={locale.NoteForSickForm}
                  initialValue={store.data?.sickPaymentFormNote || ""}
                  onChange={(v) => store.saveSickFormNote(v)}
                  isLoading={store.loading}
                />
              </ListItem>
            </List>
          )}

          {props.consultantType === "subcontractor" && (
            <List component={Card} style={{ marginTop: 16 }}>
              <ListSubheader>{locale.SubcontractorPaymentForm}</ListSubheader>

              {/*<PublishSubcontractorFlowListItem*/}
              {/*  bookingId={props.bookingId}*/}
              {/*  canPublish={true}*/}
              {/*/>*/}

              <ListItem
                button
                dense
                disabled={!store.canCreateSubcontractorForm}
                onClick={store.goToSubcontractorForm}
              >
                <ListItemText primary={locale.EditSubcontractorForm} />
                <ListItemSecondaryAction>
                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={(e) =>
                      store.openContractById(
                        e,
                        store.data?.subcontractorContractId,
                        store.data?.subcontractorContractName
                      )
                    }
                  >
                    <WithTooltip title={store.data?.subcontractorContractName}>
                      <PlaylistAddCheck />
                    </WithTooltip>
                  </IconButton>
                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={(e) => store.downloadSubcontractorForm(e)}
                  >
                    <SaveAlt />
                  </IconButton>
                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={(e) => store.previewSubcontractorForm(e)}
                  >
                    <OpenInNew />
                  </IconButton>
                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={() => setOptionsDialog("subcontractor")}
                  >
                    <SettingsOutlined />
                  </IconButton>{" "}
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem
                button
                dense
                disabled={!store.canSendSubcontractorFormToRecipient}
                onClick={() => store.sendSubcontractorFormDraftToConsultant()}
              >
                <ListItemText primary={locale.SendSubcontractorFormDraftToConsultant} />
                <ListItemSecondaryAction>
                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={(e) => store.downloadSubcontractorFormDraft(e)}
                  >
                    <SaveAlt />
                  </IconButton>

                  <IconButton
                    disabled={!store.canDownloadSubcontractorForm}
                    size={"small"}
                    onClick={(e) => store.previewSubcontractorFormDraft(e)}
                  >
                    <OpenInNew />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem
                button
                dense
                disabled={!store.canSendSubcontractoFormToFortnox}
                onClick={(e) =>
                  store.confirmAction(e, store.sendSubcontractFormToFortnox)
                }
              >
                <ListItemText primary="Skicka utkast på betalning till Fortnox" />
                {store.isCreatingFortnoxInvoice && (
                  <Spinner inline size={14} style={{ marginLeft: 8 }} />
                )}

                {props.isGroupInvoicing && (
                  <Button
                    size="small"
                    disabled={!store.canSendSubcontractoFormToFortnox}
                    onClick={(e) => {
                      subcontractorInvoiceDialog.open();
                      e.stopPropagation();
                    }}
                  >
                    <PostAddOutlined fontSize="small" /> Samlingsfaktura
                  </Button>
                )}
              </ListItem>

              <ListItem
                button
                dense
                disabled={!store.canOpenSubcontractorFormInFortnox}
                onClick={store.openSubcontractorFormInFortnox}
              >
                <ListItemText
                  primary={`Öppna betalning i Fortnox (${locale.PaymentNumber}: ${
                    store.data?.subcontractorFortnoxPaymentId ?? "n/a"
                  })`}
                />
              </ListItem>

              {store.showSubcontractorAdditionalPayoutInput && (
                <ListItem>
                  <AdditionalPayoutInput
                    id="subcontractor"
                    initialAmount={store.bookingData?.additionalPayoutAmount ?? null}
                    initialDescription={
                      store.bookingData?.additionalPayoutDescription ?? ""
                    }
                    onSave={(input) => store.saveAdditionalPayout(input)}
                    isLoading={store.loading}
                  />
                </ListItem>
              )}

              <ListItem>
                <FormNote
                  id="subcontractor-form"
                  label={locale.NoteForSubcontractorForm}
                  initialValue={store.data?.subcontractorFormNote || ""}
                  onChange={(v) => store.saveSubcontractorFormNote(v)}
                  isLoading={store.loading}
                />
              </ListItem>
            </List>
          )}

          {(props.isOwnProject || props.isProjectManager || store.canSeeAllTotals) &&
            store.showSummary && (
              <Card style={{ marginTop: 8, paddingTop: 8, marginBottom: 8 }}>
                <BookingFinanceSummary
                  fetch={store.fetchTotals}
                  loading={store.totalsLoading}
                  data={store.totals}
                  error={!!store.totalsError}
                />
              </Card>
            )}

          {invoiceDialog.dialog}
          {subcontractorInvoiceDialog.dialog}

          <Dialog
            maxWidth="xs"
            open={!!showOptionsDialog}
            onBackdropClick={() => setOptionsDialog(null)}
          >
            <DialogTitle>Välj typ</DialogTitle>
            <DialogContent>
              <div>
                <Button
                  onClick={(e) => {
                    switch (showOptionsDialog) {
                      case "invoice":
                        store.downloadCustomerInvoiceForm(e);
                        break;
                      case "payment":
                        store.downloadPaymentForm(e);
                        break;
                      case "subcontractor":
                        store.downloadSubcontractorForm(e);
                        break;
                      case "sick-payment":
                        store.downloadSickPaymentForm(e);
                        break;
                    }
                    setOptionsDialog(null);
                  }}
                >
                  Hela veckan
                </Button>
              </div>

              <div>
                <Button
                  onClick={(e) => {
                    switch (showOptionsDialog) {
                      case "invoice":
                        store.downloadCustomerInvoiceFormByShift(e);
                        break;
                      case "payment":
                        store.downloadPaymentFormByShift(e);
                        break;
                      case "subcontractor":
                        store.downloadSubcontractorFormByShift(e);
                        break;
                      case "sick-payment":
                        store.downloadSickPaymentFormByShift(e);
                        break;
                    }

                    setOptionsDialog(null);
                  }}
                >
                  Månadsbryt, per pass
                </Button>
              </div>

              <div>
                <Button
                  onClick={(e) => {
                    switch (showOptionsDialog) {
                      case "invoice":
                        store.downloadCustomerInvoiceFormByDate(e);
                        break;
                      case "payment":
                        store.downloadPaymentFormByDate(e);
                        break;
                      case "subcontractor":
                        store.downloadSubcontractorFormByDate(e);
                        break;
                      case "sick-payment":
                        store.downloadSickPaymentFormByDate(e);
                        break;
                    }
                    setOptionsDialog(null);
                  }}
                >
                  Månadsbryt, per datum
                </Button>
              </div>

              {showOptionsDialog === "subcontractor" && (
                <div>
                  <Button
                    onClick={(e) => {
                      store.downloadSubcontractorFormDraft(e);
                      setOptionsDialog(null);
                    }}
                  >
                    Hela veckan, utkast till leverantör
                  </Button>
                </div>
              )}

              {showOptionsDialog === "subcontractor" && (
                <div>
                  <Button
                    onClick={(e) => {
                      store.downloadSubcontractorFormByShiftDraft(e);
                      setOptionsDialog(null);
                    }}
                  >
                    Månadsbryt per pass, utkast till leverantör
                  </Button>
                </div>
              )}

              {showOptionsDialog === "subcontractor" && (
                <div>
                  <Button
                    onClick={(e) => {
                      store.downloadSubcontractorFormByDateDraft(e);
                      setOptionsDialog(null);
                    }}
                  >
                    Månadsbryt per datum, utkast till leverantör
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
          <Dialog
            maxWidth="xs"
            open={!!store.showPayFormSelectDayDialog}
            onBackdropClick={store.togglePayFormWeekDayDialog}
          >
            <DialogTitle>
              Konsulten har redan registrerad lön på måndagen. Vänligen välj en ledig dag
              att registrera denna lön på.
            </DialogTitle>
            <DialogContent>
              <div style={{ padding: 16 }}>
                <Select
                  label={locale.Day}
                  multi={false}
                  options={payFormWeekDayOptions.map((opt) => ({
                    ...opt,
                    disabled: store.takenWeekDays.includes(Number(opt.value))
                  }))}
                  onChange={(v) => store.handlePayFormWeekDay(v)}
                  value={store.payFormWeekDay.toString()}
                  fullWidth
                  name={`day`}
                  transformValue={(v) => Number(v.value)}
                />
              </div>
              <div style={{ display: "flex" }}>
                <Button
                  style={{ marginLeft: "auto" }}
                  color={"primary"}
                  onClick={(e) => {
                    store.sendPaymentFormToFortnox(e, store.payFormWeekDay);
                    store.togglePayFormWeekDayDialog();
                  }}
                >
                  {locale.Confirm}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog
            maxWidth="xs"
            open={!!store.showSickFormSelectDayDialog}
            onBackdropClick={store.toggleSickFormWeekDayDialog}
          >
            <DialogTitle>
              Konsulten har redan registrerad lön på måndagen. Vänligen välj en ledig dag
              att registrera sjuklönen på.
            </DialogTitle>
            <DialogContent>
              <div style={{ padding: 16 }}>
                <Select
                  label={locale.Day}
                  multi={false}
                  options={payFormWeekDayOptions.map((opt) => ({
                    ...opt,
                    disabled: store.takenWeekDays.includes(Number(opt.value))
                  }))}
                  onChange={(v) => store.handleSickFormWeekDay(v)}
                  value={store.sickFormWeekDay.toString()}
                  fullWidth
                  name={`sick-day`}
                  transformValue={(v) => Number(v.value)}
                />
              </div>
              <div style={{ display: "flex" }}>
                <Button
                  style={{ marginLeft: "auto" }}
                  color={"primary"}
                  onClick={(e) => {
                    store.sendSickFormToFortnox(e, store.sickFormWeekDay);
                    store.toggleSickFormWeekDayDialog();
                  }}
                >
                  {locale.Confirm}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog
            maxWidth="xs"
            open={!!store.showFortnoxInvoiceDialog}
            onBackdropClick={store.toggleFortnoxInvoiceDialog}
          >
            <DialogTitle>{locale.CreateInvoiceFormFortnox}</DialogTitle>
            <DialogContent>
              <Form store={store.fortnoxInvoiceForm} allowUpdates>
                <div style={{ padding: 16 }}>
                  <ToggleButtonGroup
                    name="month"
                    wrap
                    label={locale.Month}
                    options={Object.values(InvoiceMonth).map((v) => ({
                      label: locale.invoiceMonth[v],
                      value: v
                    }))}
                  />
                  <ToggleButtonGroup
                    name="type"
                    wrap
                    label={locale.Type}
                    options={Object.values(InvoiceSplitType).map((v) => ({
                      label: locale.invoiceSplitType[v],
                      value: v
                    }))}
                  />
                </div>
              </Form>
              <div style={{ display: "flex" }}>
                <Button
                  style={{ marginLeft: "auto" }}
                  color={"primary"}
                  variant={"contained"}
                  onClick={(e) => {
                    store.createFortnoxInvoice(e);
                    store.toggleFortnoxInvoiceDialog(e);
                  }}
                >
                  {locale.Send}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Observer>
  );
});

const useStyles = makeStyles(() => ({
  listItemWithInput: {
    "& div.Mui-focused input": {
      background: "white"
    },
    "& input": {
      appearance: "textfield"
    }
  }
}));
